import Button from 'react-bootstrap/Button';
import Col from 'react-bootstrap/Col';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Modal from 'react-bootstrap/Modal';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { useAppDispatch, useAppSelector } from '../../services/hooks';
import { clearCart, fetchProducts, removeProduct, setLoading, setProductQuantity } from '../../stores/cart/cartSlice';
import { formatPrice } from '../../helpers/formatPrice';
import { roundToTwo } from '../../helpers/roundToTwo';
import { useEffect } from 'react';

interface HeaderCartModalProps {
    show: boolean;
    onHide: () => void;
}

export default function HeaderCartModal({ show, onHide }: HeaderCartModalProps) {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const products = useAppSelector((state) => state.cart.products);
    const cartGrossValue = useAppSelector((state) => state.cart.valueGross);
    const currency = useAppSelector((state) => state.user.currency);
    const orderWithin = useAppSelector((state) => state.cart.orderWithin);
    const estimatedDelivery = useAppSelector((state) => state.cart.estimatedDelivery);

    const handleProductDelete = (id: number | null) => {
        if (id === null) return
        dispatch(setProductQuantity({ id: id, quantity: 0 }));
        dispatch(removeProduct(id))
    };

    async function cartClear() {
        await dispatch(clearCart());
        dispatch(fetchProducts());
    }

    const productsPreview = products.map((product, index) => (
        <Row key={index} className='basketPreviewItem align-items-center'>
            <Col xs={3}>
                <img src={`${product.img}`}
                    className="img-fluid rounded-2 shadow-sm my-2" alt="" />
            </Col>
            <Col xs={7}>
                <a href={product.url} className="link-dark text-decoration-none">
                    {product.product}
                </a>
                <div data-testid="cart_modal_products_quantity" className="mt-2">
                    <span className="fw-bold mt-2">
                        {t('header.modal.cart.quantity')}:
                    </span>
                    &nbsp;{product.quantity} {product.unit}
                </div>
            </Col>
            <Col xs={2}>
                <button onClick={() => handleProductDelete(product.id)}
                    className="btn btn-dark bi-trash3 btn-sm rounded-3" />
            </Col>
        </Row>
    ))

    useEffect(() => {
        dispatch(setLoading(true))
        dispatch(fetchProducts())
    }, [])

    return (
        <Modal show={show} onHide={onHide} /* id="basketNavModal" aria-labelledby="basketNavModal" */
            dialogClassName='modalDialogBasket'>
            <Modal.Header>
                <span className='fs-4 fw-bold m-0'>
                    {t('header.modal.cart.header')}
                </span>
                <button type="button" onClick={onHide} className="btn-close" data-testid="close_modal" aria-label="Close" />
            </Modal.Header>
            <Modal.Body>
                <Container fluid>
                    <Row className='align-items-center'>
                        <Col xs={12}
                            className='p-3 bi-ui-radios-grid text-center bg-body-tertiary border-2 border border-secondary-subtle rounded-3'>
                            <span className="small">
                                &nbsp;{t('header.modal.cart.summary.it')}
                                <strong className="cart-products-count">
                                    &nbsp;{products.length}
                                </strong>
                                &nbsp;{t('header.modal.cart.summary.product')}
                                <strong>
                                    <span className="cart-products-price">
                                        &nbsp;{formatPrice(roundToTwo(cartGrossValue / 100), currency)}
                                    </span>
                                </strong>
                            </span>
                        </Col>
                        <Col xs={12}
                            className='bg-info-subtle text-primary-emphasis p-3 my-2 bi-clock-history text-center border-2  border border-info-subtle rounded-3'>
                            <span className="small">
                                <strong>&nbsp;{t('header.modal.cart.order_within')}</strong>&nbsp;
                                {orderWithin || '-'}&nbsp;
                                <strong>{t('header.modal.cart.to_receive')}</strong>&nbsp;
                                {estimatedDelivery || '-'}
                            </span>
                        </Col>
                        <Col xs={12} className='basketPreview'>
                            {productsPreview}
                        </Col>
                    </Row>
                </Container>
            </Modal.Body>
            <Modal.Footer>
                <Button onClick={cartClear} size='sm' variant='outline-dark' className='rounded-3'>
                    {t('header.modal.cart.clear_cart')}
                </Button>
                <Link to={'cart'}>
                    <Button variant='success' onClick={onHide} className='rounded-3'>
                        {t('header.modal.cart.go_cart')}
                    </Button>
                </Link>
            </Modal.Footer>
        </Modal>
    )
}